import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  Calendar,
  Target,
  Users,
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  Filter,
  Search,
  Download,
  RefreshCw,
  TrendingUp,
  BarChart3,
  PieChart,
  Activity,
  MoreHorizontal,
  HelpCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import './MilestoneTracking.css';
import ConfirmationModal from '../common/ConfirmationModal';

// Custom Dropdown Component for Milestones (exact copy from ProjectManagement)
const MilestoneActionDropdown = ({ milestone, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      // Check if there's enough space below the button
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 80; // Approximate height of the dropdown menu
      
      // If not enough space below, open upward
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    console.log('View button clicked, milestone:', milestone);
    setIsOpen(false);
    if (!milestone) {
      console.error('Milestone is null or undefined');
      return;
    }
    
    const milestoneId = milestone._id || milestone.id;
    if (!milestoneId) {
      console.error('Milestone data is missing ID:', milestone);
      return;
    }
    
    console.log('Calling onView with milestone:', milestone);
    onView(milestone);
  };

  const handleEdit = () => {
    console.log('Edit button clicked, milestone data:', milestone);
    setIsOpen(false);
    if (!milestone) {
      console.error('Milestone is null or undefined');
      return;
    }
    
    const milestoneId = milestone._id || milestone.id;
    if (!milestoneId) {
      console.error('Milestone data is missing ID:', milestone);
      return;
    }
    
    console.log('Calling onEdit with milestone:', milestone);
    onEdit(milestone);
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (milestone && (milestone.id || milestone._id)) {
      onDelete(milestone);
    } else {
      console.error('Milestone data is missing or invalid:', milestone);
    }
  };

  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: openUpward ? window.innerHeight - rect.top + 8 : rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen, openUpward]);

  const dropdownMenu = isOpen ? (
    <ul className="dropdown-menu dropdown-menu-end show" style={{
      position: 'fixed',
      [openUpward ? 'bottom' : 'top']: `${menuPosition.top}px`,
      right: `${menuPosition.right}px`,
      zIndex: 999999,
      minWidth: '180px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      borderRadius: '0',
      padding: '0',
      backgroundColor: '#ffffff',
      listStyle: 'none',
      margin: 0,
      overflow: 'hidden',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      isolation: 'isolate'
    }}>
      <li style={{ 
        backgroundColor: '#ffffff',
        margin: 0,
        padding: 0,
        border: 'none',
        isolation: 'isolate'
      }}>
        <button
          className="dropdown-item d-flex align-items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleView();
          }}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#374151',
            border: 'none',
            backgroundColor: '#ffffff',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            transition: 'background-color 0.2s ease',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            isolation: 'isolate',
            zIndex: 10000
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
          }}
        >
          <Eye size={16} style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }} />
          View
        </button>
      </li>
      <li style={{ 
        backgroundColor: '#ffffff',
        margin: 0,
        padding: 0,
        border: 'none',
        isolation: 'isolate'
      }}>
        <button
          className="dropdown-item d-flex align-items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEdit();
          }}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#374151',
            border: 'none',
            backgroundColor: '#ffffff',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            transition: 'background-color 0.2s ease',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            isolation: 'isolate',
            zIndex: 10000
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
          }}
        >
          <Edit size={16} style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }} />
          Edit
        </button>
      </li>
      <li style={{ 
        borderTop: '1px solid #e5e7eb', 
        marginTop: '4px', 
        paddingTop: '4px',
        backgroundColor: '#ffffff',
        isolation: 'isolate'
      }}>
        <button
          className="dropdown-item d-flex align-items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDelete();
          }}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            color: '#dc2626',
            border: 'none',
            backgroundColor: '#ffffff',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
            transition: 'background-color 0.2s ease',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            isolation: 'isolate',
            zIndex: 10000
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#fef2f2';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
          }}
        >
          <Trash2 size={16} style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }} />
          Delete
        </button>
      </li>
    </ul>
  ) : null;

  return (
    <>
      <div className="dropdown" ref={dropdownRef} style={{ position: 'relative', zIndex: 1 }}>
        <button
          ref={buttonRef}
          className="btn btn-outline-secondary btn-sm"
          type="button"
          onClick={handleToggle}
          style={{
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            color: '#3A8DCC',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            height: '32px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#E8F1FD';
            e.target.style.borderColor = '#3A8DCC';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
        </button>
      </div>
      {isOpen && createPortal(dropdownMenu, document.body)}
    </>
  );
};

const MilestoneTracking = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [stats, setStats] = useState({
    totalMilestones: 0,
    completedMilestones: 0,
    overdueMilestones: 0,
    upcomingMilestones: 0,
    completionRate: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Load data
  useEffect(() => {
    loadMilestones();
    loadProjects();
  }, []);

  const loadMilestones = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/milestones`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Milestones data received:', data);
        
        if (data.success && data.milestones) {
          // Map backend milestone data to frontend format
          const mappedMilestones = data.milestones.map((milestone, index) => {
            // Calculate progress based on status or actual/estimated hours
            let progress = 0;
            if (milestone.status === 'completed' || milestone.status === 'Completed') {
              progress = 100;
            } else if (milestone.estimatedHours && milestone.actualHours) {
              progress = Math.min(Math.round((parseFloat(milestone.actualHours) / parseFloat(milestone.estimatedHours)) * 100), 100);
            } else if (milestone.status === 'in-progress' || milestone.status === 'In Progress') {
              progress = 50; // Default for in-progress
            }
            
            // Determine if overdue
            let status = milestone.status?.toLowerCase() || 'pending';
            if (milestone.dueDate) {
              const dueDate = new Date(milestone.dueDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (dueDate < today && status !== 'completed' && status !== 'Completed') {
                status = 'overdue';
              }
            }
            
            return {
              id: milestone._id || milestone.id || `milestone-${index}`,
              _id: milestone._id,
              name: milestone.name || 'Unnamed Milestone',
              description: milestone.description || '',
              projectId: milestone.projectId || milestone.project?._id,
              projectName: milestone.projectName || milestone.project?.name || 'Unknown Project',
              projectCode: milestone.projectCode || milestone.project?.projectCode,
              status: status,
              priority: milestone.priority?.toLowerCase() || 'medium',
              dueDate: milestone.dueDate || milestone.dueDate,
              startDate: milestone.startDate,
              assignedTo: milestone.assignedTo || 'Unassigned',
              progress: progress,
              estimatedHours: milestone.estimatedHours,
              actualHours: milestone.actualHours,
              deliverables: milestone.deliverables || [],
              dependencies: milestone.dependencies || [],
              risks: milestone.risks || [],
              notes: milestone.notes || ''
            };
          });
          
          setMilestones(mappedMilestones);
          
          // Update stats from backend or calculate from milestones
          if (data.stats) {
            setStats({
              totalMilestones: data.stats.totalMilestones || mappedMilestones.length,
              completedMilestones: data.stats.completedMilestones || 0,
              overdueMilestones: data.stats.overdueMilestones || 0,
              upcomingMilestones: data.stats.upcomingMilestones || 0,
              completionRate: data.stats.completionRate || 0
            });
          } else {
            // Calculate stats from milestones
            setStats({
              totalMilestones: mappedMilestones.length,
              completedMilestones: mappedMilestones.filter(m => m.status === 'completed').length,
              overdueMilestones: mappedMilestones.filter(m => m.status === 'overdue').length,
              upcomingMilestones: mappedMilestones.filter(m => m.status === 'pending' || m.status === 'in-progress').length,
              completionRate: mappedMilestones.length > 0 
                ? Math.round((mappedMilestones.filter(m => m.status === 'completed').length / mappedMilestones.length) * 100)
                : 0
            });
          }
          
          // Update pagination
          setPagination(prev => ({
            ...prev,
            totalItems: mappedMilestones.length,
            totalPages: Math.ceil(mappedMilestones.length / prev.itemsPerPage)
          }));
        } else {
          console.warn('Unexpected response format:', data);
          setMilestones([]);
          setStats(getMockStats());
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load milestones:', response.status, errorData);
        showError(errorData.message || 'Failed to load milestones');
        setMilestones([]);
        setStats(getMockStats());
      }
    } catch (err) {
      console.error('Error loading milestones:', err);
      showError('Error loading milestones. Please try again.');
      setMilestones([]);
      setStats(getMockStats());
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECTS, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const getMockMilestones = () => [
    {
      id: 1,
      name: 'Project Kickoff',
      description: 'Initial project setup and team alignment',
      projectId: 1,
      projectName: 'Digital Transformation',
      status: 'completed',
      priority: 'high',
      startDate: '2024-01-15',
      dueDate: '2024-01-20',
      completedDate: '2024-01-18',
      progress: 100,
      assignedTo: 'Sarah Johnson',
      deliverables: ['Project Charter', 'Team Structure', 'Initial Requirements'],
      dependencies: [],
      risks: []
    },
    {
      id: 2,
      name: 'Requirements Analysis',
      description: 'Gather and analyze business requirements',
      projectId: 1,
      projectName: 'Digital Transformation',
      status: 'in-progress',
      priority: 'high',
      startDate: '2024-01-21',
      dueDate: '2024-02-05',
      completedDate: null,
      progress: 65,
      assignedTo: 'Mike Chen',
      deliverables: ['Requirements Document', 'User Stories', 'Acceptance Criteria'],
      dependencies: [1],
      risks: ['Stakeholder availability', 'Scope creep']
    },
    {
      id: 3,
      name: 'System Design',
      description: 'Create system architecture and design specifications',
      projectId: 1,
      projectName: 'Digital Transformation',
      status: 'pending',
      priority: 'medium',
      startDate: '2024-02-06',
      dueDate: '2024-02-20',
      completedDate: null,
      progress: 0,
      assignedTo: 'Lisa Wang',
      deliverables: ['System Architecture', 'Database Design', 'API Specifications'],
      dependencies: [2],
      risks: ['Technical complexity', 'Integration challenges']
    },
    {
      id: 4,
      name: 'Development Phase 1',
      description: 'Core functionality development',
      projectId: 2,
      projectName: 'Mobile App Development',
      status: 'overdue',
      priority: 'critical',
      startDate: '2024-01-10',
      dueDate: '2024-01-25',
      completedDate: null,
      progress: 45,
      assignedTo: 'John Smith',
      deliverables: ['User Authentication', 'Core Features', 'Basic UI'],
      dependencies: [],
      risks: ['Resource constraints', 'Technical debt']
    }
  ];

  const getMockStats = () => ({
    totalMilestones: 4,
    completedMilestones: 1,
    overdueMilestones: 1,
    upcomingMilestones: 2,
    completionRate: 25
  });

  const handleCreateMilestone = () => {
    navigate('/projects/milestone-tracking/create');
  };

  const handleViewMilestone = (milestone) => {
    console.log('handleViewMilestone called with:', milestone);
    if (!milestone) {
      console.error('Milestone is null or undefined');
      showError('Cannot view milestone: Invalid milestone data');
      return;
    }
    
    const milestoneId = milestone._id || milestone.id;
    const projectId = milestone.projectId || milestone.project?._id || milestone.project?.id;
    
    console.log('Milestone ID:', milestoneId, 'Project ID:', projectId);
    
    if (!milestoneId) {
      console.error('Missing milestone ID:', milestone);
      showError('Cannot view milestone: Missing milestone ID');
      return;
    }
    
    // If we have projectId, navigate to project view with milestones tab
    // Otherwise, navigate to edit page as a fallback
    if (projectId) {
      const url = `/projects/view/${projectId}?tab=milestones&milestoneId=${milestoneId}`;
      console.log('Navigating to:', url);
      navigate(url);
    } else {
      console.warn('No projectId found, falling back to edit');
      // Fallback: try to edit if we can't view
      handleEditMilestone(milestone);
    }
  };

  const handleEditMilestone = (milestone) => {
    console.log('handleEditMilestone called with:', milestone);
    if (!milestone) {
      console.error('Milestone is null or undefined');
      showError('Cannot edit milestone: Invalid milestone data');
      return;
    }
    
    const milestoneId = milestone._id || milestone.id;
    const projectId = milestone.projectId || milestone.project?._id || milestone.project?.id;
    
    console.log('Milestone ID:', milestoneId, 'Project ID:', projectId);
    
    if (!milestoneId) {
      console.error('Missing milestone ID:', milestone);
      showError('Cannot edit milestone: Missing milestone ID');
      return;
    }
    
    // Prefer route with projectId if available, otherwise use standalone route
    if (projectId) {
      const url = `/projects/${projectId}/milestones/${milestoneId}/edit`;
      console.log('Navigating to:', url);
      navigate(url);
    } else {
      const url = `/milestones/edit/${milestoneId}`;
      console.log('Navigating to (no projectId):', url);
      navigate(url);
    }
  };

  const handleDeleteMilestone = (milestone) => {
    setMilestoneToDelete(milestone);
    setShowDeleteModal(true);
  };

  const confirmDeleteMilestone = async () => {
    if (!milestoneToDelete) return;
    setIsDeleting(true);
    try {
      const projectId = milestoneToDelete.projectId;
      const milestoneId = milestoneToDelete._id || milestoneToDelete.id;
      
      if (!projectId || !milestoneId) {
        throw new Error('Missing project or milestone ID');
      }

      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/milestones/${milestoneId}`,
        {
          method: 'DELETE',
          headers: API_CONFIG.HEADERS
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete milestone');
      }

      success('Milestone deleted successfully');
      setShowDeleteModal(false);
      setMilestoneToDelete(null);
      await loadMilestones();
      } catch (err) {
        console.error('Delete milestone error:', err);
        showError(err.message);
      } finally {
        setIsDeleting(false);
      }
    };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="status-icon completed" />;
      case 'in-progress':
        return <Activity size={16} className="status-icon in-progress" />;
      case 'overdue':
        return <AlertTriangle size={16} className="status-icon overdue" />;
      case 'pending':
        return <Clock size={16} className="status-icon pending" />;
      default:
        return <Clock size={16} className="status-icon pending" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'critical';
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
        return 'low';
      default:
        return 'medium';
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Milestone Tracking"
          description="Track project milestones and deliverables"
        />
        <div className="page-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading milestones...</p>
          </div>
        </div>
      </>
    );
  }

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/milestone-tracking';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} />,
      onClick: () => loadMilestones(),
      variant: 'outline',
      tooltip: 'Refresh milestones list'
    },
    {
      label: 'New Milestone',
      icon: <Plus size={18} />,
      onClick: handleCreateMilestone,
      variant: 'primary',
      tooltip: 'Create a new milestone'
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Milestone Tracking"
        description="Track project milestones and deliverables"
        actions={headerActions}
      />
      <div className="page-container">

      {/* DataTable */}
      <DataTable
        data={milestones}
        title="Milestones"
        showActions={false}
        columns={[
          {
            key: '__spacer__',
            label: '',
            width: '16px',
            sortable: false,
            render: () => <div style={{ width: '16px' }} />
          },
          {
            key: 'name',
            label: 'Milestone',
            width: '280px',
            render: (value, milestone) => (
              <div className="milestone-info">
                <div className="milestone-name">{milestone.name}</div>
                <div className="milestone-description">{milestone.description}</div>
              </div>
            )
          },
          {
            key: 'projectName',
            label: 'Project',
            width: '200px',
            render: (value) => <div className="project-name">{value}</div>
          },
          {
  key: 'status',
  label: 'Status',
  width: '140px',
  render: (value) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        whiteSpace: 'nowrap',
      }}
    >
      {getStatusIcon(value)}
      <span
        style={{
          fontSize: '0.85rem',
          textTransform: 'capitalize',
          lineHeight: 1,
        }}
      >
        {value}
      </span>
    </div>
  ),
},
          {
            key: 'priority',
            label: 'Priority',
            width: '120px',
            render: (value) => (
              <div className={`priority-badge ${getPriorityColor(value)}`}>
                {value}
              </div>
            )
          },
          {
            key: 'dueDate',
            label: 'Due Date',
            width: '150px',
            render: (value) => {
              if (!value) return <span>-</span>;
              try {
                const date = new Date(value);
                const formattedDate = date.toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                });
                return (
                  <div className="due-date">
                    <Calendar size={14} />
                    {formattedDate}
                  </div>
                );
              } catch (e) {
                return <span>{value}</span>;
              }
            }
          },
          {
            key: 'progress',
            label: 'Progress',
            width: '180px',
            render: (value) => (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${value}%` }}
                  ></div>
                </div>
                <span className="progress-text">{value}%</span>
              </div>
            )
          },
          {
            key: 'assignedTo',
            label: 'Assigned To',
            width: '160px',
            render: (value) => (
              <div className="assigned-to">
                <Users size={14} />
                {value}
              </div>
            )
          },
          {
            key: 'actions',
            label: 'Actions',
            render: (value, milestone) => {
              // Ensure milestone is the full row object
              const milestoneData = milestone || value;
              console.log('Rendering actions for milestone:', milestoneData);
              
              if (!milestoneData || (!milestoneData._id && !milestoneData.id)) {
                console.error('Invalid milestone data in actions column:', milestoneData);
                return <span style={{ color: 'red', fontSize: '12px' }}>Error</span>;
              }
              
              return (
                <MilestoneActionDropdown 
                  milestone={milestoneData}
                  onView={handleViewMilestone}
                  onEdit={handleEditMilestone}
                  onDelete={handleDeleteMilestone}
                />
              );
            }
          }
        ]}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={loading}
        emptyMessage="No milestones found"
        showCheckboxes={false}
      />
      </div>
      <ConfirmationModal
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={confirmDeleteMilestone}
      title="Delete Milestone"
      message={`Are you sure you want to delete "${milestoneToDelete?.name}"? This action cannot be undone.`}
      confirmText="Delete"
      confirmVariant="danger"
      isLoading={isDeleting}
     />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default MilestoneTracking;